import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  RotateCcw,
  Settings,
  Maximize,
  Volume2,
  VolumeX,
  AlertCircle
} from 'lucide-react';
import { GolfButton } from '../golf/golf-button';
import { GolfCard } from '../golf/golf-card';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { edgeInvoke } from '@/lib/edgeInvoke';
import { getThumbnailUrl } from '@/lib/thumbnail';
import { getPlayableUrl, isPlayableVideoUrl, getVideoDisplayInfo } from '@/lib/video/source-resolver';
// S-3022: Cloudflare Stream playback support
import { 
  resolvePlaybackUrl, 
  getThumbnailUrl as getUnifiedThumbnailUrl,
  type VideoSource 
} from '@/lib/video/cloudflare-playback-resolver';
import type { Database } from '@/integrations/supabase/types';

type Video = Database['public']['Tables']['videos']['Row'];

interface VideoPlayerProps {
  video: Video;
  className?: string;
  onAnalyzeClick?: () => void;
  maintainAspectRatio?: boolean;
  noWrapper?: boolean;
}

export function VideoPlayer({ 
  video, 
  className = "",
  onAnalyzeClick,
  maintainAspectRatio = true,
  noWrapper = false
}: VideoPlayerProps) {
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInteracted, setUserInteracted] = useState(false); // S-2204: Track user interaction for autoplay
  // S-3022: Track if using Cloudflare iframe player
  const [useIframePlayer, setUseIframePlayer] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const { toast } = useToast();

  // S-3022: Get playable video URL using unified resolver (Cloudflare + Mux)
  const getPlaybackUrl = useCallback(async () => {
    if (!video) {
      setError('No video provided');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // S-3022: Try new Cloudflare resolver first
      const videoSource: VideoSource = {
        id: video.id,
        cf_stream_uid: (video as any).cf_stream_uid ?? null,
        cf_playback_url: (video as any).cf_playback_url ?? null,
        mux_playback_id: video.mux_playback_id ?? null,
        playback_policy: video.playback_policy ?? null,
        status: video.status,
        legacy_video_id: (video as any).legacy_video_id ?? null,
      };

      const playback = resolvePlaybackUrl(videoSource);

      if (playback?.url) {
        // Cloudflare iframe format requires different rendering
        if (playback.type === 'cloudflare' && playback.format === 'iframe') {
          setVideoSrc(playback.url); // iframe URL
          setUseIframePlayer(true);
          console.log('[S-3022] Using Cloudflare iframe player');
        } else if (playback.requiresSigning && playback.type === 'mux') {
          // Mux private video - get signed URL via edge function
          const result = await getPlayableUrl(video);
          if (result.url && isPlayableVideoUrl(result.url)) {
            setVideoSrc(result.url);
            setUseIframePlayer(false);
            if (result.error) {
              setError(result.error);
            }
          } else {
            setVideoSrc(null);
            setUseIframePlayer(false);
            setError(result.error || 'Failed to get signed playback URL');
          }
        } else {
          // Cloudflare HLS or Mux public URL - use directly
          setVideoSrc(playback.url);
          setUseIframePlayer(false);
          console.log(`[S-3022] Using ${playback.type} ${playback.format} player`);
        }
      } else {
        setVideoSrc(null);
        setUseIframePlayer(false);
        setError('No playable video URL available');
      }
    } catch (error) {
      console.error('[S-3022] Error resolving video source:', error);
      setVideoSrc(null);
      setError('Failed to resolve video source');
    } finally {
      setIsLoading(false);
    }
  }, [video]);

  // S-3022: Load thumbnail (unified for Cloudflare + Mux)
  const loadThumbnail = useCallback(async () => {
    if (!video) {
      setThumbnailUrl('');
      return;
    }

    try {
      const videoSource: VideoSource = {
        id: video.id,
        cf_stream_uid: (video as any).cf_stream_uid ?? null,
        cf_playback_url: (video as any).cf_playback_url ?? null,
        mux_playback_id: video.mux_playback_id ?? null,
        playback_policy: video.playback_policy ?? null,
        status: video.status,
        legacy_video_id: (video as any).legacy_video_id ?? null,
      };

      // S-3022: Use unified thumbnail resolver (Cloudflare priority)
      const url = getUnifiedThumbnailUrl(videoSource);
      setThumbnailUrl(url);

      console.log('[S-3022] Thumbnail loaded:', {
        source: videoSource.cf_stream_uid ? 'cloudflare' : 'mux',
        url,
      });
    } catch (error) {
      console.error('[S-3022] Error loading thumbnail:', error);
      setThumbnailUrl('/placeholder.svg');
    }
  }, [video]);

  useEffect(() => {
    // S-3022: Support both Cloudflare and Mux videos
    const hasCfUid = !!(video as any)?.cf_stream_uid;
    const hasMuxId = !!video?.mux_playback_id;
    const hasPlaybackSource = hasCfUid || hasMuxId;

    if (video?.status === 'ready' && hasPlaybackSource) {
      getPlaybackUrl();
      loadThumbnail();
    } else if (video?.status === 'processing' && hasCfUid) {
      // S-3022: Cloudflare videos can be played optimistically during processing
      getPlaybackUrl();
      loadThumbnail();
    } else {
      setIsLoading(false);
      if (video?.status === 'processing') {
        setError('Video is still processing');
      } else if (video?.status === 'errored' || video?.status === 'failed') {
        setError('Video processing failed');
      } else if (!hasPlaybackSource) {
        setError('No playback source available');
      } else {
        setError('Video not ready for playback');
      }
    }
  }, [video, getPlaybackUrl, loadThumbnail]);

  // S-2204: Enhanced play handler with user interaction tracking
  const handlePlay = useCallback(() => {
    if (videoRef.current) {
      setUserInteracted(true); // Mark user interaction for autoplay
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(error => {
          console.error('Play failed:', error);
          toast({
            title: "Playback Error",
            description: "Unable to play video. Please try again.",
            variant: "destructive"
          });
        });
      }
    }
  }, [isPlaying, toast]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  // S-2204: AC1 - Auto-play when video is ready and user has interacted
  const attemptAutoPlay = useCallback(async () => {
    if (!videoRef.current || !videoSrc) return;
    
    try {
      // Try to play automatically when video loads
      await videoRef.current.play();
      setUserInteracted(true);
    } catch (error) {
      // Autoplay blocked - this is expected, user will need to click play
      console.log('Autoplay blocked, waiting for user interaction');
    }
  }, [videoSrc]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      // S-2204: AC1 - Attempt autoplay when metadata is loaded
      attemptAutoPlay();
    }
  }, [attemptAutoPlay]);

  const handleSeek = useCallback((value: number[]) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  }, []);

  const handleVolumeChange = useCallback((value: number[]) => {
    if (videoRef.current) {
      videoRef.current.volume = value[0];
      setVolume(value[0]);
      setIsMuted(value[0] === 0);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // S-2204: AC2 - Keyboard controls for accessibility
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!videoRef.current) return;
    
    // Only handle keys when video player is focused or no other input is focused
    const activeElement = document.activeElement;
    const isInputFocused = activeElement?.tagName === 'INPUT' || 
                          activeElement?.tagName === 'TEXTAREA' ||
                          activeElement?.contentEditable === 'true';
    
    if (isInputFocused) return;
    
    switch (event.code) {
      case 'Space':
        event.preventDefault();
        setUserInteracted(true);
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play().catch(console.error);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        videoRef.current.currentTime = Math.max(0, currentTime - 10);
        break;
      case 'ArrowRight':
        event.preventDefault();
        videoRef.current.currentTime = Math.min(duration, currentTime + 10);
        break;
      case 'ArrowUp': {
        event.preventDefault();
        const newVolumeUp = Math.min(1, volume + 0.1);
        videoRef.current.volume = newVolumeUp;
        setVolume(newVolumeUp);
        setIsMuted(false);
        break;
      }
      case 'ArrowDown': {
        event.preventDefault();
        const newVolumeDown = Math.max(0, volume - 0.1);
        videoRef.current.volume = newVolumeDown;
        setVolume(newVolumeDown);
        setIsMuted(newVolumeDown === 0);
        break;
      }
    }
  }, [isPlaying, currentTime, duration, volume]);

  // S-2204: AC2 - Setup keyboard event listeners (after handleKeyDown is defined)
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const skipBackward = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, currentTime - 10);
    }
  }, [currentTime]);

  const skipForward = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(duration, currentTime + 10);
    }
  }, [currentTime, duration]);

  if (isLoading) {
    return (
      <GolfCard className={cn("p-8", className)}>
        <div className="text-center">
          <div className="w-16 h-16 bg-muted rounded-lg mx-auto mb-3 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
          <h3 className="font-semibold mb-2">Loading Video</h3>
          <p className="text-sm text-muted-foreground">
            Preparing video for playback...
          </p>
        </div>
      </GolfCard>
    );
  }

  // S-2219: AC4 - Graceful UI message when no asset available
  if (!videoSrc || video?.status !== 'ready') {
    const displayInfo = video ? getVideoDisplayInfo(video) : null;
    
    return (
      <GolfCard className={cn("p-8", className)}>
        <div className="text-center">
          <div className="w-16 h-16 bg-muted rounded-lg mx-auto mb-3 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="font-semibold mb-2">
            {video?.status === 'processing' ? 'Video Processing' : 
             !displayInfo?.hasPlaybackId ? 'No Playable Asset' :
             'Video Unavailable'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {!displayInfo?.hasPlaybackId ? 
              'This video does not have a playable asset available. Please try re-uploading.' :
              error || 'Video is not ready for playback'}
          </p>
          {video?.status === 'processing' && (
            <div className="w-full bg-muted rounded-full h-2 mb-4">
              <div className="bg-primary h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
          )}
          {!displayInfo?.canPlay && video?.status !== 'processing' && (
            <GolfButton 
              variant="outline" 
              size="sm"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </GolfButton>
          )}
        </div>
      </GolfCard>
    );
  }

  // S-3022: Render iframe for Cloudflare embed or native video player
  const videoContent = (
    <>
      <div className="relative">
        {useIframePlayer ? (
          // S-3022: Cloudflare iframe player with built-in controls
          <iframe
            src={videoSrc || ''}
            className={cn("w-full bg-black border-0", maintainAspectRatio ? "aspect-video" : "")}
            style={{ border: 'none' }}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
            title="Video player"
            onError={(e) => {
              console.error('[S-3022] Iframe player error:', e);
              setError('Failed to load video player');
            }}
          />
        ) : (
          // Native HTML5 video player
          <video
            ref={videoRef}
            src={videoSrc || ''}
            className={cn("w-full bg-black", maintainAspectRatio ? "aspect-video" : "")}
            crossOrigin="anonymous"
            playsInline
            webkit-playsinline="true"
            preload="metadata"
            controls={false} // S-2219: Use custom controls for consistency
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onCanPlay={() => {
              // S-2219: Ensure controls are enabled when video can play
              if (videoRef.current) {
                console.log('Video can play - controls enabled');
              }
            }}
            onError={(e) => {
              console.error('[S-3022] Video player error:', e);
              setError('Failed to load video');
            }}
            poster={thumbnailUrl}
            // S-2219: AC2 - Make video focusable for keyboard controls
            tabIndex={0}
            aria-label="Video player - Use spacebar to play/pause, arrow keys to seek and adjust volume"
            onFocus={() => setUserInteracted(true)}
          />
        )}
        
        {/* Video Controls - S-2204: AC2 - Always visible and accessible */}
        {/* S-3022: Hide custom controls for Cloudflare iframe (has built-in controls) */}
        {!useIframePlayer && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-90 hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200">
          <div className="space-y-3">
            {/* Progress Bar */}
            <Slider
              value={[currentTime]}
              max={duration}
              step={0.1}
              onValueChange={handleSeek}
              className="w-full"
            />
            
            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <GolfButton
                  variant="ghost"
                  size="sm"
                  onClick={skipBackward}
                  className="text-white hover:text-primary"
                >
                  <SkipBack className="h-4 w-4" />
                </GolfButton>
                
                <GolfButton
                  variant="ghost"
                  size="sm"
                  onClick={handlePlay}
                  className="text-white hover:text-primary"
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </GolfButton>
                
                <GolfButton
                  variant="ghost"
                  size="sm"
                  onClick={skipForward}
                  className="text-white hover:text-primary"
                >
                  <SkipForward className="h-4 w-4" />
                </GolfButton>
                
                <div className="flex items-center space-x-2 ml-4">
                  <GolfButton
                    variant="ghost"
                    size="sm"
                    onClick={toggleMute}
                    className="text-white hover:text-primary"
                  >
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </GolfButton>
                  
                  <Slider
                    value={[isMuted ? 0 : volume]}
                    max={1}
                    step={0.1}
                    onValueChange={handleVolumeChange}
                    className="w-20"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white">
                  {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')} / {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}
                </span>
                
                {onAnalyzeClick && (
                  <GolfButton onClick={onAnalyzeClick} size="sm">
                    Analyze Swing
                  </GolfButton>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
      </div>
      
      {error && (
        <div className="p-4 border-t">
          <div className="flex items-center space-x-2 text-warning text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        </div>
      )}
    </>
  );

  // Conditional wrapper
  if (noWrapper) {
    return (
      <div className={cn("overflow-hidden", className)}>
        {videoContent}
      </div>
    );
  }

  return (
    <GolfCard className={cn("overflow-hidden", className)}>
      {videoContent}
    </GolfCard>
  );
}